/**
 * Data Access Layer for Programs
 *
 * Programs are date-ranged groupings of happenings across multiple stages.
 * They're account-scoped: slug is unique per account, and account_id is
 * stamped from the current account context on create.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type {
  Program,
  ProgramCreate,
  ProgramUpdate,
  ProgramPlace,
  ProgramPlaceCreate,
} from './types.js';

// Fire-and-forget webhook when a program changes, so SSR consumers (e.g.
// the website) can revalidate their cached timetable.
async function notifyProgramUpdated(programId: string, reason: string): Promise<void> {
  if (typeof window !== 'undefined') return; // server-side only
  try {
    const { triggerWebsiteUpdate } = await import('./webhook.js');
    triggerWebsiteUpdate('program-updated', { programId, reason });
  } catch (err) {
    console.warn('[programs] failed to fire program-updated webhook', err);
  }
}

// ============================================
// Programs
// ============================================

export async function getProgram(id: string): Promise<Program | null> {
  const { data, error } = await getClient()
    .from(TABLES.programs)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as Program;
}

export async function getProgramBySlug(slug: string): Promise<Program | null> {
  let query = getClient().from(TABLES.programs).select('*').eq('slug', slug);
  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);

  const { data, error } = await query.single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as Program;
}

export async function listPrograms(filters?: {
  start_time_gte?: string;
  start_time_lte?: string;
  query?: string;
}): Promise<Program[]> {
  let query = getClient().from(TABLES.programs).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);

  if (filters?.start_time_gte) query = query.gte('start_time', filters.start_time_gte);
  if (filters?.start_time_lte) query = query.lte('start_time', filters.start_time_lte);
  if (filters?.query && filters.query.trim()) {
    const term = filters.query.trim();
    query = query.or(`name.ilike.%${term}%,slug.ilike.%${term}%`);
  }

  const { data, error } = await query.order('start_time', { ascending: false });

  if (error) throw error;
  return (data || []) as Program[];
}

export async function createProgram(input: ProgramCreate): Promise<Program> {
  const accountId = getCurrentAccountId();
  if (!accountId) {
    throw new Error('createProgram: no account context set');
  }

  const { data, error } = await getClient()
    .from(TABLES.programs)
    .insert({
      ...input,
      account_id: accountId,
      settings: input.settings ?? {},
    })
    .select()
    .single();

  if (error) throw error;
  const program = data as Program;
  void notifyProgramUpdated(program.id, 'program-created');
  return program;
}

export async function updateProgram(id: string, input: ProgramUpdate): Promise<Program> {
  const { data, error } = await getClient()
    .from(TABLES.programs)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  const program = data as Program;
  void notifyProgramUpdated(program.id, 'program-updated');
  return program;
}

export async function deleteProgram(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.programs)
    .delete()
    .eq('id', id);

  if (error) throw error;
  void notifyProgramUpdated(id, 'program-deleted');
}

// ============================================
// Program ↔ Place links (stages in a program)
// ============================================

export async function listProgramPlaces(programId: string): Promise<ProgramPlace[]> {
  const { data, error } = await getClient()
    .from(TABLES.program_places)
    .select('*')
    .eq('program_id', programId)
    .order('sort_order');

  if (error) throw error;
  return (data || []) as ProgramPlace[];
}

export async function addProgramPlace(input: ProgramPlaceCreate): Promise<ProgramPlace> {
  const { data, error } = await getClient()
    .from(TABLES.program_places)
    .insert({
      program_id: input.program_id,
      place_id: input.place_id,
      sort_order: input.sort_order ?? 0,
    })
    .select()
    .single();

  if (error) throw error;
  const link = data as ProgramPlace;
  void notifyProgramUpdated(link.program_id, 'stage-added');
  return link;
}

export async function removeProgramPlace(programId: string, placeId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.program_places)
    .delete()
    .eq('program_id', programId)
    .eq('place_id', placeId);

  if (error) throw error;
  void notifyProgramUpdated(programId, 'stage-removed');
}

export async function setProgramPlaces(programId: string, placeIds: string[]): Promise<void> {
  // Replace the program's stage list in one go: delete current rows, insert new.
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.program_places)
    .delete()
    .eq('program_id', programId);
  if (deleteError) throw deleteError;

  if (placeIds.length > 0) {
    const rows = placeIds.map((place_id, idx) => ({
      program_id: programId,
      place_id,
      sort_order: idx,
    }));

    const { error: insertError } = await client.from(TABLES.program_places).insert(rows);
    if (insertError) throw insertError;
  }

  void notifyProgramUpdated(programId, 'stages-updated');
}
